import { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { authApi } from '../lib/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('pitchpal_token'));
  const [loading, setLoading] = useState(true);
  const [pending2FA, setPending2FA] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      const storedToken = localStorage.getItem('pitchpal_token');
      const savedUser = localStorage.getItem('pitchpal_user');
      
      if (storedToken && savedUser) {
        setToken(storedToken);
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
        } catch (e) {
          localStorage.removeItem('pitchpal_token');
          localStorage.removeItem('pitchpal_user');
          setToken(null);
        }
        
        try {
          const response = await authApi.getMe();
          setUser(response.data);
          localStorage.setItem('pitchpal_user', JSON.stringify(response.data));
        } catch (error) {
          if (error.response?.status === 401) {
            localStorage.removeItem('pitchpal_token');
            localStorage.removeItem('pitchpal_user');
            setUser(null);
            setToken(null);
          }
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = useCallback(async (email, password) => {
    const response = await authApi.login({ email, password });
    const data = response.data;
    
    if (data.requires_2fa) {
      setPending2FA({
        session_id: data.session_id,
        email_hint: data.email_hint
      });
      return { requires_2fa: true, email_hint: data.email_hint };
    }
    
    const { access_token, user } = data;
    localStorage.setItem('pitchpal_token', access_token);
    localStorage.setItem('pitchpal_user', JSON.stringify(user));
    setToken(access_token);
    setUser(user);
    return user;
  }, []);

  const verify2FA = useCallback(async (code) => {
    if (!pending2FA) throw new Error('No pending 2FA session');
    
    const response = await authApi.verify2FA({
      session_id: pending2FA.session_id,
      code
    });
    const { access_token, user } = response.data;
    localStorage.setItem('pitchpal_token', access_token);
    localStorage.setItem('pitchpal_user', JSON.stringify(user));
    setToken(access_token);
    setUser(user);
    setPending2FA(null);
    return user;
  }, [pending2FA]);

  const resend2FA = useCallback(async () => {
    if (!pending2FA) throw new Error('No pending 2FA session');
    const response = await authApi.resend2FA({ session_id: pending2FA.session_id });
    return response.data;
  }, [pending2FA]);

  const cancel2FA = useCallback(() => {
    setPending2FA(null);
  }, []);

  const register = useCallback(async (email, password, organizationName) => {
    const response = await authApi.register({
      email,
      password,
      organization_name: organizationName,
    });
    const { access_token, user, recovery_key } = response.data;
    localStorage.setItem('pitchpal_token', access_token);
    localStorage.setItem('pitchpal_user', JSON.stringify(user));
    setToken(access_token);
    setUser(user);
    return { user, recovery_key };
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('pitchpal_token');
    localStorage.removeItem('pitchpal_user');
    setUser(null);
    setToken(null);
  }, []);

  const refreshUser = useCallback(async () => {
    try {
      const response = await authApi.getMe();
      setUser(response.data);
      localStorage.setItem('pitchpal_user', JSON.stringify(response.data));
      return response.data;
    } catch (error) {
      console.error('Error refreshing user:', error);
      return null;
    }
  }, []);

  const contextValue = useMemo(() => ({
    user, token, loading, pending2FA,
    login, verify2FA, resend2FA, cancel2FA,
    register, logout, refreshUser
  }), [user, token, loading, pending2FA, login, verify2FA, resend2FA, cancel2FA, register, logout, refreshUser]);

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
